import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import loyaltyService from '../../services/loyaltyService';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import ProgressOverview from './components/ProgressOverview';
import CommunityActivity from './components/CommunityActivity';
import TierBenefits from './components/TierBenefits';
import QuickActions from './components/QuickActions';
import WellnessInsights from './components/WellnessInsights';
import NotificationCenter from './components/NotificationCenter';
import Icon from '../../components/AppIcon';

const MemberDashboard = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [walletData, setWalletData] = useState(null);

  // Load wallet data for loyalty integration
  useEffect(() => {
    const loadWalletData = async () => {
      if (!user?.id) return;
      
      try {
        const { data } = await loyaltyService?.getUserWallet(user?.id);
        setWalletData(data);
      } catch (error) {
        console.error('Failed to load wallet data:', error);
      }
    };

    if (user?.id) {
      loadWalletData();
    }
  }, [user?.id]);

  // Mock user data (integrate with auth)
  const mockUser = {
    id: user?.id || 1,
    name: userProfile?.full_name || user?.email?.split('@')?.[0] || "Sarah Johnson",
    email: user?.email || "sarah.johnson@email.com",
    tier: walletData?.loyalty_tier || "All Access",
    memberSince: "March 2024",
    avatar: userProfile?.avatar_url || "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face"
  };

  // Mock progress data - replace with real data from Supabase
  const progressData = {
    currentStreak: 12,
    weeklyGoals: { completed: 4, total: 6 },
    monthlyProgress: 78,
    achievements: [
      { name: "7-Day Streak", icon: "Flame", date: "Oct 1" },
      { name: "Mindful Monday", icon: "Brain", date: "Oct 7" },
      { name: "Community Helper", icon: "Heart", date: "Oct 5" },
      { name: "Goal Crusher", icon: "Target", date: "Oct 3" }
    ]
  };

  // Mock community activities
  const communityActivities = [
    {
      id: 1,
      type: "forum",
      author: "Michael Chen",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      content: "Just completed my first 30-day meditation challenge! The mental clarity and reduced stress levels have been incredible. Anyone else trying mindfulness practices?",
      time: "2 hours ago",
      likes: 24,
      replies: 8
    },
    {
      id: 2,
      type: "forum",
      author: "Emma Rodriguez",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      content: "Looking for workout buddies for morning yoga sessions. I\'ve been practicing for 6 months and would love to share the journey with others in our community.",
      time: "4 hours ago",
      likes: 18,
      replies: 12
    },
    {
      id: 3,
      type: "event",
      author: "Dr. Lisa Park",
      avatar: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=150&h=150&fit=crop&crop=face",
      content: "Excited to announce our upcoming workshop on 'Nutrition for Mental Wellness'. We'll explore the connection between diet and mood, plus practical meal planning tips.",
      time: "6 hours ago",
      likes: 35,
      replies: 15
    }
  ];

  // Mock upcoming events
  const upcomingEvents = [
    {
      id: 1,
      title: "Mindful Morning Meditation",
      date: "Oct 10",
      time: "7:00 AM",
      attendees: 45,
      type: "virtual"
    },
    {
      id: 2,
      title: "Nutrition Workshop: Healthy Meal Prep",
      date: "Oct 12",
      time: "2:00 PM",
      attendees: 28,
      type: "in-person"
    }
  ];

  // Enhanced tier information with loyalty data
  const tierInfo = {
    currentTier: walletData?.loyalty_tier || "All Access",
    nextTier: "Flex",
    progress: { current: walletData?.lifetime_earned || 850, required: 1200 },
    benefits: [
      { name: "Unlimited Workshop Access", description: "Join any workshop or event" },
      { name: "Community Forums", description: "Full access to all discussion groups" },
      { name: "Progress Tracking", description: "Advanced analytics and insights" },
      { name: "Expert Consultations", description: "Monthly 1-on-1 sessions" }
    ],
    exclusiveContent: [
      {
        title: "Advanced Meditation Techniques",
        description: "Master-level practices for experienced practitioners",
        icon: "Brain",
        link: "/wellness-marketplace?category=meditation"
      },
      {
        title: "Personalized Nutrition Plans",
        description: "Custom meal plans based on your wellness goals",
        icon: "Apple",
        link: "/wellness-marketplace?category=nutrition"
      }
    ]
  };

  // Enhanced wellness insights
  const wellnessInsights = [
    {
      title: "Consistent Progress",
      description: "You\'ve maintained a 12-day streak! Your consistency is paying off with improved wellness metrics.",
      type: "positive",
      timeframe: "Last 2 weeks"
    },
    {
      title: "Sleep Pattern Improvement",
      description: "Your sleep quality has improved by 23% since starting the evening meditation routine.",
      type: "positive",
      timeframe: "Last month"
    },
    {
      title: "Loyalty Tokens Available",
      description: `You have ${loyaltyService?.formatTokens(walletData?.current_balance || 0)} tokens available for rewards!`,
      type: "info",
      timeframe: "Current balance"
    }
  ];

  // Mock active challenges
  const activeChallenges = [
    {
      name: "October Mindfulness Challenge",
      participants: 234,
      daysLeft: 23,
      progress: 65
    },
    {
      name: "Healthy Habits Tracker",
      participants: 156,
      daysLeft: 15,
      progress: 82
    }
  ];

  // Enhanced notifications with loyalty alerts
  const notifications = [
    {
      id: 1,
      title: "Tokens Earned!",
      message: "You earned 15 tokens for completing your weekly exercise goal. Keep up the great work!",
      category: "achievements",
      time: "30 minutes ago",
      read: false
    },
    {
      id: 2,
      title: "New Reward Available",
      message: "A new premium workshop access reward is now available in your loyalty wallet.",
      category: "rewards",
      time: "2 hours ago",
      read: false
    },
    {
      id: 3,
      title: "Achievement Unlocked!",
      message: "Congratulations! You\'ve earned the \'7-Day Streak\' badge for maintaining consistent daily activities.",
      category: "achievements",
      time: "1 day ago",
      read: true
    }
  ];

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);

    return () => clearInterval(timer);
  }, []);

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
    // Navigate to relevant section based on notification type
    if (notification?.category === 'achievements' || notification?.category === 'rewards') {
      window.location.href = '/loyalty-wallet';
    }
  };

  const handleNotificationAction = (action) => {
    console.log('Notification action:', action);
  };

  const handleQuickAction = (action) => {
    console.log('Quick action:', action);
    // Navigate to loyalty wallet for token-related actions
    if (action === 'view_tokens' || action === 'earn_tokens') {
      window.location.href = '/loyalty-wallet';
    }
  };

  const getGreeting = () => {
    const hour = currentTime?.getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading dashboard...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header 
        user={mockUser}
        notifications={notifications}
        onNotificationClick={handleNotificationClick}
        onProfileClick={() => window.location.href = '/member-profile'}
      />
      <div className="flex">
        <Sidebar 
          isCollapsed={isSidebarCollapsed}
          onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          user={mockUser}
        />
        
        <main className={`flex-1 transition-all duration-300 ${
          isSidebarCollapsed ? 'ml-16' : 'ml-64'
        } p-6`}>
          {/* Welcome Section */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground">
                  {getGreeting()}, {mockUser?.name?.split(' ')?.[0]}!
                </h1>
                <p className="text-muted-foreground mt-1">
                  Welcome to your wellness dashboard. Here's your progress overview for today.
                </p>
              </div>
              <div className="hidden md:flex items-center space-x-4">
                <div className="text-right">
                  <p className="text-sm font-medium text-foreground">
                    {currentTime?.toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {currentTime?.toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </p>
                </div>
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
                  <Icon name="Sun" size={24} color="white" />
                </div>
              </div>
            </div>

            {/* Loyalty Tokens Quick View */}
            {walletData && (
              <div 
                onClick={() => window.location.href = '/loyalty-wallet'}
                className="mb-6 p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg border border-primary/20 cursor-pointer hover:from-primary/15 hover:to-accent/15 transition-all duration-200"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                      <Icon name="Coins" size={20} color="white" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Loyalty Balance</p>
                      <p className="text-xl font-bold text-foreground">
                        {loyaltyService?.formatTokens(walletData?.current_balance)} tokens
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-primary">{walletData?.loyalty_tier} Member</p>
                    <p className="text-xs text-muted-foreground">Tap to manage wallet</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Dashboard Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-6">
              <ProgressOverview user={mockUser} progressData={progressData} />
              <CommunityActivity 
                activities={communityActivities} 
                upcomingEvents={upcomingEvents} 
              />
              <WellnessInsights 
                insights={wellnessInsights} 
                challenges={activeChallenges} 
              />
            </div>

            {/* Right Column - Sidebar Content */}
            <div className="space-y-6">
              <TierBenefits user={mockUser} tierInfo={tierInfo} />
              <QuickActions onActionClick={handleQuickAction} />
              <NotificationCenter 
                notifications={notifications}
                onNotificationAction={handleNotificationAction}
              />
            </div>
          </div>

          {/* Bottom Section - Enhanced Stats with Loyalty */}
          <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-card rounded-lg border border-border p-4 text-center shadow-wellness">
              <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="Coins" size={20} color="white" />
              </div>
              <p className="text-2xl font-heading font-bold text-foreground">
                {loyaltyService?.formatTokens(walletData?.current_balance || 0)}
              </p>
              <p className="text-sm text-muted-foreground">Loyalty Tokens</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 text-center shadow-wellness">
              <div className="w-12 h-12 bg-success rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="Award" size={20} color="white" />
              </div>
              <p className="text-2xl font-heading font-bold text-foreground">23</p>
              <p className="text-sm text-muted-foreground">Achievements</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 text-center shadow-wellness">
              <div className="w-12 h-12 bg-accent rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="Users" size={20} color="white" />
              </div>
              <p className="text-2xl font-heading font-bold text-foreground">89</p>
              <p className="text-sm text-muted-foreground">Community Posts</p>
            </div>

            <div className="bg-card rounded-lg border border-border p-4 text-center shadow-wellness">
              <div className="w-12 h-12 bg-secondary rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="TrendingUp" size={20} color="white" />
              </div>
              <p className="text-2xl font-heading font-bold text-foreground">92%</p>
              <p className="text-sm text-muted-foreground">Goal Success</p>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MemberDashboard;